import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { useGuidedSetup } from '../../services/hooks/useGuidedSetup';
import { setupStepIds, SetupStepId, SetupStepStatus } from '../../services/helpers/guidedSetup';
import SetupStepCard from '../../components/molecules/GuidedSetup/SetupStepCard';
import SendAgreementModal from '../../components/molecules/GuidedSetup/SendAgreementModal';
import HeaderContent from '../../components/atoms/Layout/HeaderContent';
import CenterContent from '../../components/atoms/Layout/CenterContent';
import { workflowAction } from '../../components/molecules/Admin/Merchants/merchantWorkflows';
import OnboardStoreForm from '../../components/molecules/Admin/Stores/OnboardStoreForm';
import CreateApiSecretForm from '../../components/molecules/Admin/ApiSecrets/CreateApiSecretForm';
import { openMerchantWorkflow } from '../../services/helpers/openMerchantWorkflow';
import { LockedModal } from '../../components/atoms/Modal/LockedModal';
import Snackbar from '../../components/atoms/Snackbar/Snackbar';
import Spinner from '../../components/atoms/Spinner/Spinner';
import ErrorMessage from '../../components/atoms/Message/Error/ErrorMessage';
import { useAppDispatch } from '../../store';
import sessionActions from '../../store/session/actions';
import { useOnboardingStore } from '../../store/admin/onboarding/onboardingStore';
import { useApiSecretsStore } from '../../store/admin/apiSecrets/apiSecretsStore';
import { listMerchants, onboardStore } from '../../api/onboarding/onboarding';
import { createApiSecret } from '../../api/apiSecrets/apiSecrets';
import {
    getAgreementStatus,
    isAgreementSigned,
    Merchant,
    MerchantBankAccountStatus,
    MerchantKycStatus,
    MerchantWorkflow,
} from '../../types/onboarding/Merchant';
import { MerchantAgreementStatus } from '../../types/onboarding/MerchantAgreement';
import ResponseError from '../../types/response/ResponseError';
import { ErrorType } from '../../types/response/ErrorCodes';
import { getOnboardingCountryName } from '../../constants/onboardingCountries';

interface GuidedSetupPageProps {
    theme: DefaultTheme;
}

type SetupModal = 'agreement' | 'store' | 'apiKey';

const agreementLabels: Record<MerchantAgreementStatus, string> = {
    [MerchantAgreementStatus.NotSent]: 'Send agreement',
    [MerchantAgreementStatus.Pending]: 'Awaiting signatures',
    [MerchantAgreementStatus.Signed]: 'Agreement signed',
    [MerchantAgreementStatus.AlreadySigned]: 'Agreement signed',
    [MerchantAgreementStatus.Failed]: 'Send agreement again',
};

const agreementReasons: Partial<Record<MerchantAgreementStatus, string>> = {
    [MerchantAgreementStatus.Pending]:
        'The agreement has been emailed to the signatories and awaits their signatures',
};

const kycLabels: Record<MerchantKycStatus, string> = {
    [MerchantKycStatus.NotAvailable]: 'Start verification',
    [MerchantKycStatus.NotStarted]: 'Start verification',
    [MerchantKycStatus.InProgress]: 'Continue verification',
    [MerchantKycStatus.InReview]: 'Being reviewed',
    [MerchantKycStatus.Approved]: 'Company verified',
    [MerchantKycStatus.Declined]: 'Verification declined',
};

const kycReasons: Partial<Record<MerchantKycStatus, string>> = {
    [MerchantKycStatus.NotAvailable]: 'Company verification is not available yet. Contact Walley.',
    [MerchantKycStatus.InReview]: 'Walley is reviewing your company details',
    [MerchantKycStatus.Approved]: 'Your company is verified',
    [MerchantKycStatus.Declined]: 'We could not verify your company. Contact Walley.',
};

const bankAccountLabels: Record<MerchantBankAccountStatus, string> = {
    [MerchantBankAccountStatus.NotAvailable]: 'Add payout account',
    [MerchantBankAccountStatus.NotStarted]: 'Add payout account',
    [MerchantBankAccountStatus.Pending]: 'Continue adding account',
    [MerchantBankAccountStatus.Verified]: 'Payout account added',
    [MerchantBankAccountStatus.Failed]: 'Could not be verified',
};

const bankAccountReasons: Partial<Record<MerchantBankAccountStatus, string>> = {
    [MerchantBankAccountStatus.NotAvailable]:
        'Adding a payout account is not available yet. Contact Walley.',
    [MerchantBankAccountStatus.Verified]: 'Payout account added',
    [MerchantBankAccountStatus.Failed]: 'We could not verify the bank account. Contact Walley.',
};

/** Why a Bits workflow cannot be opened from the setup, or undefined when it can. */
const workflowDisabledReason = <S extends string>(
    merchant: Merchant | undefined,
    workflow: MerchantWorkflow,
    reasons: Partial<Record<S, string>>,
    status: S,
    notAvailable: S
): string | undefined => {
    if (!merchant) return reasons[notAvailable];
    if (workflowAction(merchant, workflow)?.opens) return undefined;

    return reasons[status] ?? 'Not available';
};

const workflowErrorTypes: Record<MerchantWorkflow, ErrorType> = {
    [MerchantWorkflow.Kyc]: ErrorType.StartKyc,
    [MerchantWorkflow.BankAccount]: ErrorType.StartBankAccountVerification,
};

/**
 * The guided setup of a new organization: five ordered steps from signed agreement to API key. The landing page of
 * organizations the backend sends to the setup (shouldRedirectToGetStarted), with a done banner once every step is done.
 */
const GuidedSetupPage: React.FC<GuidedSetupPageProps> = ({ theme }: GuidedSetupPageProps) => {
    const dispatch = useAppDispatch();
    const setup = useGuidedSetup();
    const { organization, isSetupLoaded } = setup;

    // The backend stops sending the organization to the setup once every step is done; when that happens during the
    // visit, the page stays with the done banner instead of leaving.
    const wasInSetup = useRef(false);
    if (setup.isEnabled) wasInSetup.current = true;
    const isInSetup = setup.isEnabled || wasInSetup.current;

    const {
        onboardedStore,
        isOnboardingStore,
        onboardStoreStarted,
        onboardStoreSucceeded,
        onboardStoreFailed,
        clearOnboardStore,
        clearBankAccounts,
        listMerchantsStarted,
        listMerchantsSucceeded,
        listMerchantsFailed,
        isSendingAgreement,
        clearAgreement,
    } = useOnboardingStore();

    const {
        isCreating: isCreatingApiKey,
        creating,
        created,
        creatingFailed,
        createdCleared,
        searchStarted,
        searchSucceeded,
        searchFailed,
    } = useApiSecretsStore();

    const [modal, setModal] = useState<SetupModal | undefined>(undefined);
    const [startingWorkflow, setStartingWorkflow] = useState<MerchantWorkflow | undefined>();
    const [workflowError, setWorkflowError] = useState<
        { workflow: MerchantWorkflow; error: ResponseError } | undefined
    >();
    const [message, setMessage] = useState('');

    const { refresh } = setup;

    // KYC and bank account happen in other tabs (or by other people), so the steps are re-read whenever the user comes
    // back to this one. Not while a modal is open: the user is in the middle of something, and the modal must not be
    // handed reloaded data. The steps are re-read when the modal closes instead (closeModal), which also shows what
    // the modal did.
    useEffect(() => {
        if (modal) return;

        window.addEventListener('focus', refresh);

        return () => window.removeEventListener('focus', refresh);
    }, [modal, refresh]);

    const closeModal = () => {
        setModal(undefined);
        refresh();
    };

    useEffect(() => {
        if (!onboardedStore) return;

        setMessage(
            `${onboardedStore.presentationName} (${
                onboardedStore.salesSegment
            }, ${getOnboardingCountryName(onboardedStore.countryCode)}) is onboarded.`
        );
        clearOnboardStore();
        clearBankAccounts();
        setModal(undefined);
        if (organization) dispatch(sessionActions.getOrganization(organization.id, true));
    }, [onboardedStore, clearOnboardStore, clearBankAccounts, dispatch, organization]);

    // The page only exists for organizations in the setup, which is not known until the organization is loaded.
    if (isSetupLoaded && (!isInSetup || !organization)) return <Navigate to="/orders" replace />;

    if (!isSetupLoaded || !organization || setup.isLoading)
        return (
            <Centered>
                <Spinner color={theme.colors.primary} loading />
            </Centered>
        );

    const { merchant, steps, isDone } = setup;
    const kycStatus = merchant?.kycStatus ?? MerchantKycStatus.NotAvailable;
    const bankAccountStatus = merchant?.bankAccountStatus ?? MerchantBankAccountStatus.NotAvailable;
    const startWorkflow = (workflow: MerchantWorkflow) => {
        if (!merchant) return;

        openMerchantWorkflow(
            organization.id,
            merchant,
            workflow,
            () => {
                setStartingWorkflow(workflow);
                setWorkflowError(undefined);
            },
            () => setStartingWorkflow(undefined),
            (failedWorkflow, error) => {
                setStartingWorkflow(undefined);
                setWorkflowError({ workflow: failedWorkflow, error });
            }
        );
    };

    const openStore = () => {
        // The onboard store form picks the merchant and its bank accounts from the onboarding store.
        listMerchants(
            organization.id,
            listMerchantsStarted,
            listMerchantsSucceeded,
            listMerchantsFailed
        );
        setModal('store');
    };

    const hideStore = () => {
        if (isOnboardingStore) return;

        clearOnboardStore();
        clearBankAccounts();
        closeModal();
    };

    const hideApiKey = () => {
        if (isCreatingApiKey) return;

        createdCleared();
        closeModal();
    };

    const hideAgreement = () => {
        if (isSendingAgreement) return;

        clearAgreement();
        closeModal();
    };

    const agreementStatus = merchant
        ? getAgreementStatus(merchant)
        : MerchantAgreementStatus.NotSent;
    const isSigned = !!merchant && isAgreementSigned(merchant);
    // A store and an API key are of no use before the agreement is signed, so their modals wait for it.
    const unsignedReason = isSigned ? undefined : 'Available once the agreement is signed';

    /** The agreement step action: sends the agreement, or tells why it cannot be sent; none once it is signed. */
    const agreementAction = () => {
        if (isSigned) return undefined;

        if (!merchant)
            return {
                label: agreementLabels[agreementStatus],
                disabledReason: 'Sending the agreement is not available yet. Contact Walley.',
                onClick: () => undefined,
            };

        return {
            label: agreementLabels[agreementStatus],
            disabledReason: agreementReasons[agreementStatus],
            onClick: () => setModal('agreement'),
        };
    };

    return (
        <main data-testid="guided-setup">
            <HeaderContent>
                <CenterContent>
                    <Intro>
                        <IntroText>
                            <Eyebrow>{organization.name} · Get started</Eyebrow>
                            <Heading>Set up your account to start taking payments</Heading>
                            <Lead>
                                Five steps to get your company ready to take payments with Walley.
                                You can leave and come back at any time.
                            </Lead>
                        </IntroText>
                        <Progress>
                            <ProgressLabel>
                                <span>Progress</span>
                                <span data-testid="setup-progress">
                                    {setup.doneCount} of {setupStepIds.length} done
                                </span>
                            </ProgressLabel>
                            <Track
                                role="progressbar"
                                aria-label="Setup progress"
                                aria-valuemin={0}
                                aria-valuemax={setupStepIds.length}
                                aria-valuenow={setup.doneCount}
                            >
                                <Fill $done={setup.doneCount} $total={setupStepIds.length} />
                            </Track>
                        </Progress>
                    </Intro>
                </CenterContent>
            </HeaderContent>

            <Body>
                {setup.loadError && (
                    <ErrorMessage error={setup.loadError} errorHeader={ErrorType.LoadMerchants} />
                )}

                {workflowError && (
                    <ErrorMessage
                        error={workflowError.error}
                        errorHeader={workflowErrorTypes[workflowError.workflow]}
                    />
                )}

                <Cards>
                    <SetupStepCard
                        id={SetupStepId.Agreement}
                        number={1}
                        status={steps[SetupStepId.Agreement]}
                        title="Sign the agreement"
                        description="Walley emails the agreement to the people who may sign for your company."
                        doneLabel="Signed"
                        failedLabel="Not signed"
                        statusLine={isSigned ? 'Signed' : undefined}
                        action={agreementAction()}
                    />
                    <SetupStepCard
                        id={SetupStepId.Kyc}
                        number={2}
                        status={steps[SetupStepId.Kyc]}
                        title="Verify your company"
                        description="By law, we have to check who owns and runs your company before you can take payments. Opens in a new tab."
                        doneLabel="Approved"
                        failedLabel="Declined"
                        action={{
                            label: kycLabels[kycStatus],
                            disabledReason: workflowDisabledReason(
                                merchant,
                                MerchantWorkflow.Kyc,
                                kycReasons,
                                kycStatus,
                                MerchantKycStatus.NotAvailable
                            ),
                            opensInNewTab: true,
                            onClick: () => startWorkflow(MerchantWorkflow.Kyc),
                        }}
                        isBusy={startingWorkflow === MerchantWorkflow.Kyc}
                    />
                    <SetupStepCard
                        id={SetupStepId.BankAccount}
                        number={3}
                        status={steps[SetupStepId.BankAccount]}
                        title="Add your payout account"
                        description="The bank account where Walley pays out the money from your sales. Opens in a new tab."
                        action={{
                            label: bankAccountLabels[bankAccountStatus],
                            disabledReason: workflowDisabledReason(
                                merchant,
                                MerchantWorkflow.BankAccount,
                                bankAccountReasons,
                                bankAccountStatus,
                                MerchantBankAccountStatus.NotAvailable
                            ),
                            opensInNewTab: true,
                            onClick: () => startWorkflow(MerchantWorkflow.BankAccount),
                        }}
                        isBusy={startingWorkflow === MerchantWorkflow.BankAccount}
                    />
                    <SetupStepCard
                        id={SetupStepId.Store}
                        number={4}
                        status={steps[SetupStepId.Store]}
                        title="Set up your store"
                        description="A store is where you sell, online or in a physical shop. Choose the country, what you sell and which account gets paid. The web address you add is shown on your customers’ invoices."
                        action={{
                            label:
                                steps[SetupStepId.Store] === SetupStepStatus.Done
                                    ? 'Add another store'
                                    : 'Set up store',
                            disabledReason: unsignedReason,
                            onClick: openStore,
                        }}
                    />
                    <SetupStepCard
                        id={SetupStepId.ApiKey}
                        number={5}
                        status={steps[SetupStepId.ApiKey]}
                        title="Connect your website"
                        description="Create an API key: a password your developer uses to add Walley payments to your website."
                        action={{
                            label: 'Create API key',
                            disabledReason: unsignedReason,
                            onClick: () => setModal('apiKey'),
                        }}
                    />
                </Cards>

                {isDone && (
                    <DoneBanner data-testid="setup-done">
                        You’re all set. You can now take payments with Walley.
                    </DoneBanner>
                )}

                <LockedModal medium isShown={modal === 'store'} hide={hideStore}>
                    <OnboardStoreForm
                        organizationId={organization.id}
                        organizationName={organization.name}
                        onOnboard={request =>
                            onboardStore(
                                request,
                                onboardStoreStarted,
                                onboardStoreSucceeded,
                                onboardStoreFailed
                            )
                        }
                        hide={hideStore}
                    />
                </LockedModal>

                <LockedModal
                    dataTestId="api-secret-modal"
                    focusLock
                    small
                    isShown={modal === 'apiKey'}
                    hide={hideApiKey}
                >
                    <CreateApiSecretForm
                        onSave={(label: string, expirationDate: Date | null) =>
                            createApiSecret(
                                organization.id,
                                label,
                                expirationDate,
                                creating,
                                created,
                                searchStarted,
                                searchSucceeded,
                                searchFailed,
                                creatingFailed
                            )
                        }
                        hide={hideApiKey}
                        locked={false}
                    />
                </LockedModal>

                {merchant && modal === 'agreement' && (
                    <LockedModal
                        dataTestId="merchant-agreement-modal"
                        medium
                        isShown
                        hide={hideAgreement}
                    >
                        <SendAgreementModal
                            organizationId={organization.id}
                            merchant={merchant}
                            hide={hideAgreement}
                        />
                    </LockedModal>
                )}

                {message && <Snackbar open message={message} clear={() => setMessage('')} />}
            </Body>
        </main>
    );
};

const Centered = styled.div`
    display: flex;
    justify-content: center;
    padding: 8rem 0;
`;

const Body = styled(CenterContent)`
    display: flex;
    flex-direction: column;
    gap: 3.2rem;
    padding-top: 3.2rem;
    padding-bottom: 8rem;
`;

const Intro = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-end;
    gap: 2.4rem;
`;

const IntroText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    max-width: 64rem;
`;

// Same label style as the Manage Organization header.
const Eyebrow = styled.div`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
`;

const Heading = styled.h1`
    margin: 0;
    font-family: ${props => props.theme.text.font.bold};
    font-size: 2.6rem;
    line-height: 1.2;
    color: ${props => props.theme.colors.text.primary};
`;

const Lead = styled.p`
    margin: 0;
    font-size: ${props => props.theme.text.size.medium};
    line-height: 1.5;
    color: ${props => props.theme.colors.text.secondary};
`;

const Progress = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    min-width: 22rem;
`;

const ProgressLabel = styled.div`
    display: flex;
    justify-content: space-between;
    font-family: ${props => props.theme.text.font.medium};
    color: ${props => props.theme.colors.text.primary};
`;

// The header behind the bar is subtle.light, so the empty part of the track uses the page background.
const Track = styled.div`
    height: 0.8rem;
    border-radius: 1rem;
    overflow: hidden;
    background: ${props => props.theme.background};
    box-shadow: inset 0 0 0 0.1rem ${props => props.theme.colors.border};
`;

// The gradient is stretched over the whole track, so the colour of the fill reflects how far the setup has come.
const Fill = styled.div<{ $done: number; $total: number }>`
    height: 100%;
    width: ${props => (props.$done / props.$total) * 100}%;
    border-radius: 1rem;
    background: linear-gradient(90deg, #f2e21c 0%, #f39a12 30%, #e8481f 60%, #d4107e 100%);
    background-size: ${props => (props.$done ? (props.$total / props.$done) * 100 : 100)}% 100%;
    transition: width 0.4s ease;
`;

const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
    gap: 1.6rem;
`;

const DoneBanner = styled.div`
    padding: 2rem 2.4rem;
    border: 0.1rem solid ${props => props.theme.colors.success};
    border-radius: 1rem;
    font-family: ${props => props.theme.text.font.medium};
    font-size: ${props => props.theme.text.size.large};
    color: ${props => props.theme.colors.text.primary};
    background: ${props => `${props.theme.colors.success}1a`};
`;

export default withTheme(GuidedSetupPage) as React.ComponentType<
    Omit<GuidedSetupPageProps, 'theme'>
>;
